/**
 * Ключи запросов планирования — в ОДНОМ месте.
 *
 * Почему отдельный модуль. Назначение техкарты живёт в `techCards.ts`, а весь
 * экран назначения и сводка читаются из `planVariant.ts`: после любой правки
 * назначений обязаны протухнуть ОБА неймспейса, иначе кнопка срабатывает, а
 * экран показывает прежние цифры (ревью head-web по #803 — ровно этот дефект).
 * Взаимные импорты между двумя модулями дали бы цикл, а литералы вида
 * `['planning','plan-variant']` в чужом файле разъезжаются молча при первой же
 * смене ключа. Поэтому ключи вынесены сюда, а оба модуля их переэкспортируют
 * для своих потребителей.
 */

export const techCardKeys = {
  all: () => ['planning', 'tech-cards'] as const,
  list: (seasonId: string | null, includeArchived: boolean) =>
    [
      'planning',
      'tech-cards',
      'list',
      seasonId ?? 'none',
      includeArchived ? 'with-archived' : 'active',
    ] as const,
  card: (cardId: string | null) => ['planning', 'tech-cards', 'card', cardId ?? 'none'] as const,
  preview: (cardId: string, blockKind: string, sourceId: string) =>
    ['planning', 'tech-cards', 'preview', cardId, blockKind, sourceId] as const,
  assignments: (scenarioId: string | null) =>
    ['planning', 'tech-cards', 'assignments', scenarioId ?? 'none'] as const,
};

/**
 * Структура посевных площадей — СРЕЗ того же варианта плана. Ключ живёт здесь,
 * а не в `spp/sppApi.ts`, по той же причине, что и остальные: заведение
 * культуры в план пишется из экрана назначения, а протухнуть обязана и СПП —
 * иначе соседний экран показывает структуру, которой уже нет.
 */
export const cropStructureKeys = {
  all: () => ['planning', 'spp'] as const,
  structure: (scenarioId: string | null) =>
    ['planning', 'spp', 'structure', scenarioId ?? 'none'] as const,
};

export const planVariantKeys = {
  all: () => ['planning', 'plan-variant'] as const,
  summary: (scenarioId: string | null) =>
    ['planning', 'plan-variant', 'summary', scenarioId ?? 'none'] as const,
};

/**
 * СПИСКИ вариантов плана хозяйства на сезон (`GET /planning/scenarios`).
 *
 * Один и тот же список кормит переключатели ДВУХ экранов — назначения техкарт
 * (`techCards.usePlanVariants`) и СПП (`sppApi.useSppPlanVariants`). После
 * «Создать вариант плана» протухнуть обязаны оба, иначе новый вариант виден на
 * одном экране и отсутствует на соседнем. Поэтому ключ общий и живёт здесь.
 */
export const variantListKeys = {
  all: () => ['planning', 'scenarios'] as const,
  list: (farmId: string | null, seasonId: string | null) =>
    ['planning', 'scenarios', farmId ?? 'none', seasonId ?? 'none'] as const,
};

/**
 * Системы техкарты (AK-773, решение founder Р35) — защита / питание /
 * обработка почвы как самостоятельные мини-техкарты.
 *
 * Ключ здесь по той же причине, что и у соседей: вставка системы в карту
 * меняет СОСТАВ и деньги самой карты, поэтому после неё протухают ОБА
 * неймспейса — и системы (счётчик «где используется»), и техкарты
 * (`techCardKeys`, ₽/га карты). Односторонняя инвалидация давала бы экран,
 * где система «вставлена», а стоимость карты прежняя.
 */
export const techCardModuleKeys = {
  all: () => ['planning', 'tech-card-modules'] as const,
  list: (seasonId: string | null, farmId: string | null, kind: string | null, cropId: string | null) =>
    [
      'planning',
      'tech-card-modules',
      'list',
      seasonId ?? 'none',
      farmId ?? 'any',
      kind ?? 'any',
      cropId ?? 'any',
    ] as const,
  module: (moduleId: string | null) =>
    ['planning', 'tech-card-modules', 'module', moduleId ?? 'none'] as const,
  ofCard: (cardId: string | null) =>
    ['planning', 'tech-card-modules', 'of-card', cardId ?? 'none'] as const,
  insertPreview: (cardId: string, moduleId: string) =>
    ['planning', 'tech-card-modules', 'insert-preview', cardId, moduleId] as const,
  replacePreview: (cardId: string, oldId: string, newId: string) =>
    ['planning', 'tech-card-modules', 'replace-preview', cardId, oldId, newId] as const,
};

/**
 * Сравнение вариантов ПП и решение по бюджету (UC-5, этап 5).
 *
 * Ключ здесь, а не в компоненте: утверждение и возврат меняют состояние,
 * которое читают ЧЕТЫРЕ соседа — сводка варианта (`planVariantKeys`), списки
 * вариантов (`variantListKeys`, там живёт «план сезона»), обзор СПП
 * (`sppOverviewKeys`, он берёт основной вариант) и само сравнение. После
 * решения обязаны протухнуть все, иначе кнопка сработала, а на соседнем
 * экране прежний утверждённый вариант.
 */
export const programCompareKeys = {
  all: () => ['planning', 'program-compare'] as const,
  compare: (farmId: string | null, seasonId: string | null) =>
    ['planning', 'program-compare', farmId ?? 'none', seasonId ?? 'none'] as const,
};

/**
 * «Обзор СПП» (AK-740) — drill-down company/cluster/farm.
 *
 * Отдельный неймспейс, а не литералы в компоненте (ревью head-web #892 п.4):
 * пометка «основной вариант» (★, `useSetPrimaryVariant`) пишет в ту же
 * таблицу `scenarios`, что кормит и `variantListKeys` (переключатели других
 * экранов), и этот обзор — после отметки протухнуть обязаны ОБА неймспейса.
 */
export const sppOverviewKeys = {
  all: () => ['planning', 'spp-overview'] as const,
  overview: (level: string, id: string | null, variantIds: readonly string[]) =>
    ['planning', 'spp-overview', level, id ?? 'none', [...variantIds].sort()] as const,
};
